import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useJunoContext } from '../../../contexts/JunoContext';
import { config } from '../../../config';
import api from '../../../utils/api';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useRoomContext,
  useLocalParticipant,
  TrackToggle,
  useRemoteParticipants,
  useIsSpeaking,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { toast } from 'react-toastify';
import styles from './JunoOrb.module.css';

interface JunoOrbProps {
  className?: string;
}

const JunoOrb: React.FC<JunoOrbProps> = ({ className = '' }) => {
  const {
    isJunoActive,
    setJunoActive,
    viewType,
    viewContext,
    updateViewContext,
    clearViewContext
  } = useJunoContext();
  
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [token, setToken] = useState<string>('');
  const [roomName, setRoomName] = useState<string>('');
  const [isJunoSpeaking, setIsJunoSpeaking] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const location = useLocation();
  const hasGreeted = useRef(false);

  // Update view context based on current route
  useEffect(() => {
    // Don't update context while navigating
    if (isNavigating) {
      console.log('[JUNO-DEBUG] Skipping context update - navigation in progress');
      return;
    }

    const path = location.pathname;
    console.log('[JUNO-DEBUG] Updating view context for path:', path);
    
    if (path.includes('/signals/') || path.includes('/vault/')) {
      const signalId = path.includes('/signals/') 
        ? path.split('/signals/')[1] 
        : path.split('/vault/')[1];
      updateViewContext('signal', { signalId });
    } else if (path.includes('/signals')) {
      updateViewContext('signals', {});
    } else if (path.includes('/today')) {
      // For today view, the dashboard hook will update context with summary data
      updateViewContext('today', {});
    } else if (path.includes('/reporting')) {
      updateViewContext('reporting', {});
    } else if (path.includes('/settings')) {
      updateViewContext('settings', {});
    } else {
      clearViewContext();
    }
  }, [location.pathname, updateViewContext, clearViewContext, isNavigating]);

  // Clear navigation flag after route change completes
  useEffect(() => {
    if (isNavigating) {
      console.log('[JUNO-DEBUG] Clearing navigation flag after route change');
      const timer = setTimeout(() => {
        setIsNavigating(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isNavigating]);

  const handleOrbClick = async () => {
    if (isConnected || token) {
      // Disconnect
      setToken('');
      setRoomName('');
      setIsConnected(false);
      setJunoActive(false);
      hasGreeted.current = false;
    } else {
      // Connect
      setIsConnecting(true);
      try {
        // Send current view context with token request
        const requestData: any = {};
        if (viewType) {
          requestData.view_type = viewType;
          
          // Only send signal ID if viewing a signal, not the full signal data
          if (viewType === 'signal' && viewContext.signalData) {
            const signalData = viewContext.signalData as any;
            requestData.view_context = {
              signalId: signalData.signal_id || viewContext.signalId
            };
          } else if (viewType === 'signal' && viewContext.signalId) {
            requestData.view_context = {
              signalId: viewContext.signalId
            };
          } else {
            requestData.view_context = viewContext;
          }
        }
        
        const response = await api.getApi().post('/v1/livekit/token', requestData);
        
        if (response.data.user_token && response.data.room_name) {
          setToken(response.data.user_token);
          setRoomName(response.data.room_name);
          setIsConnected(true);
          setJunoActive(true);
        } else {
          toast.error('Failed to get voice connection token');
        }
      } catch (error) {
        console.error('Failed to connect to Juno:', error);
        toast.error('Failed to connect to Juno');
      } finally {
        setIsConnecting(false);
      }
    }
  };

  const getContextDisplay = () => {
    if (!viewType) return 'Ready';
    
    switch (viewType) {
      case 'signal':
        return viewContext.signalData?.summary || 'Signal Details';
      case 'signals':
        return 'Signals';
      case 'today':
        return 'Today';
      case 'reporting':
        return 'Reporting';
      case 'settings':
        return 'Settings';
      default:
        return 'Ready';
    }
  };

  const getOrbColor = () => {
    if (isConnecting) return 'bg-yellow-400';
    if (isConnected) return 'bg-green-400';
    return 'bg-blue-400';
  };

  const getGlowEffect = () => {
    if (isConnecting) return 'animate-pulse';
    if (isJunoSpeaking) return 'animate-pulse scale-110';
    if (isConnected) return '';
    return 'hover:scale-105 transition-all duration-300';
  };

  // Component to detect when Juno is speaking - split into two components
  // to handle the conditional hook usage properly
  const JunoSpeakingDetector: React.FC<{ participant: any }> = ({ participant }) => {
    const isSpeaking = useIsSpeaking(participant);
    
    useEffect(() => {
      setIsJunoSpeaking(isSpeaking);
    }, [isSpeaking]);
    
    return null;
  };
  
  const JunoSpeakingIndicator: React.FC = () => {
    const remoteParticipants = useRemoteParticipants();
    
    // Find Juno agent participant
    const junoParticipant = remoteParticipants.find((p: any) => 
      p.identity === 'agent' || p.identity.toLowerCase().includes('juno')
    );
    
    // Only render the detector if we have a participant
    if (!junoParticipant) {
      return null;
    }
    
    return <JunoSpeakingDetector participant={junoParticipant} />;
  };

  // Component to handle room connection and context sending
  const RoomConnection: React.FC<{ setIsNavigating: (value: boolean) => void }> = ({ setIsNavigating }) => {
    const room = useRoomContext();
    const { localParticipant } = useLocalParticipant();
    const lastSentContext = useRef<string | null>(null);
    const navigate = useNavigate();
    const navigationPending = useRef(false);

    // Debug logging
    console.log('[JUNO-DEBUG] RoomConnection render:', {
      roomState: room?.state,
      roomName: room?.name,
      localParticipantSid: localParticipant?.sid,
      localParticipantIdentity: localParticipant?.identity,
      viewType,
      hasViewContext: !!viewContext
    });

    // Send view context when connected
    const sendViewContext = useCallback(async () => {
      console.log('[JUNO-DEBUG] sendViewContext called:', {
        viewType,
        roomState: room?.state,
        hasLocalParticipant: !!localParticipant,
        viewContextKeys: viewContext ? Object.keys(viewContext) : [],
        navigationPending: navigationPending.current
      });

      // Don't send context if navigation is pending
      if (navigationPending.current) {
        console.log('[JUNO-DEBUG] Navigation pending, skipping context send');
        return;
      }

      if (!viewType || !room || room.state !== 'connected' || !localParticipant) {
        console.log('[JUNO-DEBUG] sendViewContext early return:', {
          noViewType: !viewType,
          noRoom: !room,
          roomNotConnected: room?.state !== 'connected',
          noLocalParticipant: !localParticipant
        });
        return;
      }

      try {
        // Only send signal ID if we have one, not the full signal data
        let contextData = { ...viewContext };
        if (viewType === 'signal' && contextData.signalData) {
          // Extract just the ID from signal data
          const signalData = contextData.signalData as any; // Type assertion to avoid TS error
          const signalId = signalData.signal_id || contextData.signalId;
          contextData = { signalId };
          console.log('[JUNO-DEBUG] Extracted signal ID:', signalId);
        }

        const contextMsg = JSON.stringify({
          type: 'viewContext',
          viewType,
          data: contextData,
          timestamp: Date.now()
        });
        
        // Only send if context has changed
        if (contextMsg !== lastSentContext.current) {
          console.log('[JUNO-DEBUG] Publishing context data:', contextMsg);
          await localParticipant.publishData(
            new TextEncoder().encode(contextMsg),
            { reliable: true }
          );
          lastSentContext.current = contextMsg;
          console.log('[JUNO] View context sent:', { viewType, data: contextData });
        } else {
          console.log('[JUNO-DEBUG] Context unchanged, not sending');
        }
      } catch (err) {
        console.error('[JUNO] Failed to send view context:', err);
      }
    }, [viewType, viewContext, room, localParticipant, navigationPending]);

    // Send context when room connects or view changes
    useEffect(() => {
      console.log('[JUNO-DEBUG] Connection useEffect triggered:', {
        roomState: room?.state,
        hasLocalParticipant: !!localParticipant
      });
      
      if (room?.state === 'connected' && localParticipant) {
        console.log('[JUNO-DEBUG] Room connected, scheduling context send...');
        // Send initial context after a short delay
        const timer = setTimeout(() => {
          console.log('[JUNO-DEBUG] Timer fired, sending context now');
          sendViewContext();
        }, 500);
        return () => clearTimeout(timer);
      }
    }, [room?.state, localParticipant, sendViewContext]);
    
    // Also send context whenever viewType or viewContext changes
    useEffect(() => {
      console.log('[JUNO-DEBUG] ViewType/Context useEffect triggered:', {
        roomState: room?.state,
        hasLocalParticipant: !!localParticipant,
        viewType,
        hasViewContext: !!viewContext
      });
      
      if (room?.state === 'connected' && localParticipant && viewType) {
        // Add a small delay to ensure route has fully updated
        const timer = setTimeout(() => {
          console.log('[JUNO-DEBUG] Sending context due to view change (delayed)');
          sendViewContext();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [viewType, viewContext, room?.state, localParticipant, sendViewContext]);


    // Play greeting sound when first connected
    useEffect(() => {
      if (room?.state === 'connected' && !hasGreeted.current) {
        hasGreeted.current = true;
        console.log('[JUNO] Connected to room, agent should greet now');
        console.log('[JUNO-DEBUG] Room details:', {
          name: room?.name,
          sid: room?.sid,
          participants: room?.participants?.size
        });
      }
    }, [room?.state]);

    // Handle navigation messages from Juno
    useEffect(() => {
      if (!room) return;

      const handleDataReceived = (data: Uint8Array, participant?: any) => {
        try {
          const message = new TextDecoder().decode(data);
          const messageData = JSON.parse(message);
          
          console.log('[JUNO-DEBUG] Received data message:', messageData);
          
          if (messageData.type === 'navigate' && messageData.signalId) {
            console.log('[JUNO] Navigating to signal:', messageData.signalId);
            console.log('[JUNO] Signal ID length:', messageData.signalId.length);
            
            // Avoid truncation by ensuring we use the full ID
            const fullSignalId = messageData.signalId;
            console.log('[JUNO] Full signal ID for navigation:', fullSignalId);
            
            // Set navigation flags to prevent sending stale context
            navigationPending.current = true;
            setIsNavigating(true);
            
            // Navigate using the full signal ID
            const url = `/v3/vault/${fullSignalId}`;
            console.log('[JUNO] Navigating to URL:', url);
            navigate(url);
            
            toast.success('Navigating to Record...');
            // Clear the local flag after navigation completes
            setTimeout(() => {
              navigationPending.current = false;
            }, 200);
          }
        } catch (error) {
          console.error('[JUNO] Error handling data message:', error);
        }
      };

      // Add event listener for data received
      room.on('dataReceived', handleDataReceived);

      // Cleanup
      return () => {
        room.off('dataReceived', handleDataReceived);
      };
    }, [room, navigate]);

    return null;
  };

  // If we have a token, wrap everything in LiveKitRoom
  if (token && roomName) {
    return (
      <LiveKitRoom
        token={token}
        serverUrl={config.LIVEKIT_URL}
        connect={true}
        audio={true}
        video={false}
        options={{
          adaptiveStream: true,
          dynacast: true,
          publishDefaults: {
            audioPreset: {
              maxBitrate: 32000,
            },
          },
        }}
      >
        <RoomAudioRenderer />
        <RoomConnection setIsNavigating={setIsNavigating} />
        <JunoSpeakingIndicator />
        
        <div className={`fixed top-6 right-6 z-50 ${className}`}>
          {/* Juno Orb with speech bubble design */}
          <div className="relative flex items-center">
            {/* Speech Bubble with Context Display */}
            <div className="relative mr-3">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg border border-gray-200/30 min-w-[120px]">
                <div className="text-sm text-gray-600 font-medium leading-tight">
                  {isJunoSpeaking ? (
                    <span className="flex items-center gap-2">
                      <span className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </span>
                      Speaking...
                    </span>
                  ) : (
                    getContextDisplay()
                  )}
                </div>
              </div>
              {/* Speech bubble tail */}
              <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-white/95 border-r border-b border-gray-200/30 transform rotate-45 translate-x-1/2"></div>
            </div>

            {/* Main Orb Button with Good Outcomes gradient */}
            <button
              onClick={handleOrbClick}
              disabled={isConnecting}
              className={`
                relative w-14 h-14 rounded-full
                ${getGlowEffect()}
                flex items-center justify-center
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                disabled:opacity-50 disabled:cursor-not-allowed
                transform transition-all duration-500
                group
              `}
              aria-label={isConnected ? 'Disconnect from Juno' : 'Connect to Juno'}
            >
              {/* Outer glow layer */}
              <div className={`absolute inset-0 rounded-full ${styles['pulse-glow']}`}>
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 blur-xl" />
              </div>
              
              {/* Main gradient orb */}
              <div className="absolute inset-0 rounded-full overflow-hidden">
                <div 
                  className={`
                    absolute inset-0 rounded-full
                    ${isConnected 
                      ? styles['gradient-connected']
                      : isConnecting 
                      ? styles['gradient-connecting']
                      : styles['gradient-orb']
                    }
                    ${isJunoSpeaking ? 'animate-pulse' : ''}
                  `}
                >
                  {/* Rotating gradient overlay for dynamic effect */}
                  <div className="absolute inset-0 rounded-full overflow-hidden">
                    <div className="absolute inset-[-50%] rounded-full opacity-50"
                      style={{
                        background: 'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.3), transparent, rgba(255,255,255,0.3), transparent)',
                        animation: 'spin 8s linear infinite'
                      }}
                    />
                  </div>
                </div>
                
                {/* Inner shimmer effect */}
                <div className={`absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-transparent ${styles['shimmer-effect']}`} />
                
                {/* Depth shadow */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30 rounded-full" />
              </div>
              
              {/* Glass effect overlay */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/30 via-white/10 to-transparent" />
              {/* Center icon */}
              <div className="relative z-10">
                {isConnecting ? (
                  <div className="w-6 h-6 border-2 border-white/90 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <div className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                    <div className={`
                      w-4 h-4 rounded-full 
                      ${isConnected ? 'bg-gradient-to-br from-green-400 to-emerald-500' : 'bg-gradient-to-br from-blue-400 to-cyan-500'}
                      ${isJunoActive || isConnected ? 'animate-pulse' : ''}
                    `} />
                  </div>
                )}
              </div>

              {/* Connection Status Ring */}
              <div className="absolute -inset-1 rounded-full">
                <div className={`
                  absolute inset-0 rounded-full border-2 
                  ${isConnected ? 'border-green-400' : isConnecting ? 'border-yellow-400' : 'border-transparent'}
                  ${isConnected || isConnecting ? 'animate-ping' : ''}
                `} />
              </div>
            </button>

            {/* Microphone Toggle (when connected) */}
            {isConnected && (
              <div className="absolute -bottom-2 -right-2 z-10">
                <TrackToggle
                  source={Track.Source.Microphone}
                  className="w-8 h-8 rounded-full bg-white/95 backdrop-blur-sm shadow-lg border border-gray-200/30 
                           flex items-center justify-center hover:bg-white transition-all duration-200 text-gray-600
                           hover:scale-105"
                />
              </div>
            )}
          </div>
        </div>
      </LiveKitRoom>
    );
  }

  // Default view when not connected
  return (
    <div className={`fixed top-6 right-6 z-50 ${className}`}>
      {/* Juno Orb with speech bubble design */}
      <div className="relative flex items-center">
        {/* Speech Bubble with Context Display */}
        <div className="relative mr-3">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg border border-gray-200/30 min-w-[120px]">
            <div className="text-sm text-gray-600 font-medium leading-tight">
              {isJunoSpeaking ? (
                <span className="flex items-center gap-2">
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </span>
                  Speaking...
                </span>
              ) : (
                getContextDisplay()
              )}
            </div>
          </div>
          {/* Speech bubble tail */}
          <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-white/95 border-r border-b border-gray-200/30 transform rotate-45 translate-x-1/2"></div>
        </div>

        {/* Main Orb Button with Good Outcomes gradient */}
        <button
          onClick={handleOrbClick}
          disabled={isConnecting}
          className={`
            relative w-14 h-14 rounded-full
            ${getGlowEffect()}
            flex items-center justify-center
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
            disabled:opacity-50 disabled:cursor-not-allowed
            transform transition-all duration-500
            group
          `}
          aria-label={isConnected ? 'Disconnect from Juno' : 'Connect to Juno'}
        >
          {/* Outer glow layer */}
          <div className={`absolute inset-0 rounded-full ${styles['pulse-glow']}`}>
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 blur-xl" />
          </div>
          
          {/* Main gradient orb */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            <div 
              className={`
                absolute inset-0 rounded-full
                ${isConnected 
                  ? 'bg-gradient-to-br from-green-400 via-emerald-400 to-teal-400' 
                  : isConnecting 
                  ? 'bg-gradient-to-br from-yellow-400 via-amber-400 to-orange-400' 
                  : 'bg-gradient-to-br from-blue-500 via-blue-400 to-cyan-400'
                }
                ${isJunoSpeaking ? 'animate-pulse' : ''}
              `}
            />
            
            {/* Inner shimmer effect */}
            <div className={`absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-transparent ${styles['shimmer-effect']}`} />
            
            {/* Depth shadow */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 rounded-full" />
          </div>
          
          {/* Glass effect overlay */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 to-transparent" />

          {/* Center icon */}
          <div className="relative z-10">
            {isConnecting ? (
              <div className="w-6 h-6 border-2 border-white/90 border-t-transparent rounded-full animate-spin" />
            ) : (
              <div className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                <div className={`
                  w-4 h-4 rounded-full 
                  ${isConnected ? 'bg-gradient-to-br from-green-400 to-emerald-500' : 'bg-gradient-to-br from-blue-400 to-cyan-500'}
                  ${isJunoActive || isConnected ? 'animate-pulse' : ''}
                `} />
              </div>
            )}
          </div>

          {/* Connection Status Ring */}
          <div className="absolute -inset-1 rounded-full">
            <div className={`
              absolute inset-0 rounded-full border-2 
              ${isConnected ? 'border-green-400' : isConnecting ? 'border-yellow-400' : 'border-transparent'}
              ${isConnected || isConnecting ? 'animate-ping' : ''}
            `} />
          </div>
        </button>
      </div>
    </div>
  );
};

export default JunoOrb;
